import React, { ReactElement } from 'react';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';
import useAuth from '../auth/authContext';
import { AppRoute, AppRoutes } from '../router/AppRoutes';
import VisibleResource from './VisibleResource';
import {
  AiStarsIcon,
  BookIcon,
  BulbIcon,
  CalendarIcon,
  CheckboxCheckedIcon,
  LogoGrowegy,
  PowerIcon,
  QuestionMark,
  SettingsIcon,
} from '../assets/icons';
import clsx from 'clsx';
import {
  useIsNewVersionPersonalRecommendations,
  usePersonalRecommendations,
} from '../recommendations/hooks/usePersonalRecommendations';
import { ICurrentUser } from '../currentuser/currentUser';
import { Permission } from '../auth/permissions';
import { growegyAIConfig } from '../commons/config';
import { StyleVariables } from '../commons/styleConstants';
import { SanityChecker } from '../meta/components/SanityChecker';
import { useHasFetchedPastDueInvoices } from '../billing/hooks/useFetchedBillingInvoices';
import { useIsTabletOrLarger } from '../responsive/hooks/hooks';
import { SmsNotificationsPrompt } from '../billing/components/SmsNotificationsPrompt';
import { GrowegyMenu } from '../commons/components/GrowegyMenu';
import { openUrlInNewTab } from '../util/window-utils';
import useAnalytics from '../web-analytics/webAnalyticsContext';
import { AnalyticsEvent } from '../web-analytics/AnalyticsEvent';
import { UserAvatar } from '../commons/components/UserAvatar';
import { useCurrentUser } from '../user/hooks/useCurrentUser';
import { extractUserName } from '../user/utils';

const SideBarItem = (props: {
  header: string;
  icon: (props: { fill: string }) => ReactElement;
  isActive: boolean;
  subheader?: string;
  badge?: string | null;
}) => {
  const { header, icon, isActive, subheader, badge } = props;
  const color = isActive ? StyleVariables.neutral900 : StyleVariables.neutral500;
  return (
    <div className="app-sidebar__item">
      <div
        className={clsx('app-sidebar__item-icon', { active: isActive })}
        data-test="app-sidebar__item-icon"
      >
        {icon({ fill: color })}
        {badge && (
          <div className="app-sidebar__item-indicator-container">
            <div className="app-sidebar__item-indicator">
              <span
                className="app-sidebar__item-indicator-number"
                data-test="app-sidebar__badge-value"
              >
                {badge}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="app-sidebar__item-header" data-test="side-bar__link-header" style={{ color }}>
        {header}
      </div>
      {subheader ? <div className="app-sidebar__item-badge">{subheader}</div> : null}
    </div>
  );
};

const SideBarLink = (props: {
  to: AppRoute;
  alternativeTo?: AppRoute;
  secondAlternativeTo?: AppRoute;
  header: string;
  icon: (props: { fill: string }) => ReactElement;
  subheader?: string;
  badge?: string | null;
}) => {
  const { to, alternativeTo, secondAlternativeTo, header, icon, subheader, badge } = props;
  const isPrimaryActive = useMatch(to)!!;
  const isAlternativeActive = useMatch(alternativeTo ?? 'not-matching-path');
  const isSecondAlternativeToActive = useMatch(secondAlternativeTo ?? 'not-matching-path');
  const isActive = !!(
    isPrimaryActive ||
    (alternativeTo && isAlternativeActive) ||
    (secondAlternativeTo && isSecondAlternativeToActive)
  );
  return (
    <NavLink className="side-bar__link" data-test="side-bar__link" to={to}>
      <SideBarItem
        header={header}
        icon={icon}
        isActive={isActive}
        subheader={subheader}
        badge={badge}
      />
    </NavLink>
  );
};

const SideBarLogo = () => (
  <div className="app-sidebar__logo" data-test="app-sidebar__logo">
    <img src={LogoGrowegy} alt="" />
  </div>
);

const SideBarCalendar = () => {
  return (
    <VisibleResource
      to={AppRoute.calendar}
      render={(to) => (
        <SideBarLink
          to={to}
          header="Planning"
          icon={(props: { fill: string }) => <CalendarIcon fill={props.fill} />}
        />
      )}
    />
  );
};

const SideBarGrowegyAI = (props: { user: ICurrentUser; asHome?: boolean }) => {
  const { user, asHome } = props;
  if (!growegyAIConfig.enabled || !user.has(Permission.USE_CHAT_GPT)) return null;

  return (
    <VisibleResource
      to={asHome ? AppRoutes.Home : AppRoutes.GrowegyAI}
      render={(to) => (
        <SideBarLink
          to={to}
          alternativeTo={AppRoutes.GrowegyAI}
          header="Growegy AI"
          icon={(props: { fill: string }) => <AiStarsIcon fill={props.fill} />}
        />
      )}
    />
  );
};

const SideBarRecommendations = (props: { user: ICurrentUser }) => {
  const { user } = props;
  const hasNewRecommendations = useIsNewVersionPersonalRecommendations();
  const { data: recommendations } = usePersonalRecommendations('');

  if (
    !user.has(Permission.GET_TENANT_RECOMMENDATIONS) ||
    !recommendations ||
    recommendations.recommendations.length === 0
  )
    return null;

  return (
    <VisibleResource
      to={AppRoutes.Recommendations}
      render={(to) => (
        <SideBarLink
          to={to}
          header="Insights"
          icon={(props: { fill: string }) => <BulbIcon fill={props.fill} />}
          badge={hasNewRecommendations ? '1' : null}
        />
      )}
    />
  );
};

const SideBarSettings = (props: { asHome?: boolean }) => {
  const hasPastDueInvoices = useHasFetchedPastDueInvoices();
  const { asHome } = props;
  return (
    <VisibleResource
      to={asHome ? AppRoute.home : AppRoutes.Settings}
      render={(to) => (
        <SideBarLink
          to={to}
          secondAlternativeTo={AppRoute.settings}
          alternativeTo={AppRoute.billing}
          header="Settings"
          icon={(props: { fill: string }) => <SettingsIcon fill={props.fill} />}
          badge={hasPastDueInvoices ? '1' : null}
        />
      )}
    />
  );
};

const SideBarHelp = () => {
  const analytics = useAnalytics();
  const isTabletOrLarger = useIsTabletOrLarger();
  return (
    <VisibleResource
      to={AppRoute.home}
      render={() => (
        <GrowegyMenu
          menuItems={[
            {
              type: 'standard',
              text: 'Tutorials',
              icon: <BookIcon />,
              onClick: () =>
                openUrlInNewTab('https://www.growegy.com/resources/', {
                  analytics,
                  analyticsEvent: AnalyticsEvent.TUTORIALS_OPENED,
                }),
            },
            {
              type: 'standard',
              text: 'Releases',
              icon: <CheckboxCheckedIcon />,
              onClick: () =>
                openUrlInNewTab('https://www.growegy.com/resources/support/releases/', {
                  analytics,
                  analyticsEvent: AnalyticsEvent.RELEASES_OPENED,
                }),
            },
            {
              type: 'divider',
            },
            {
              type: 'custom',
              component: () => (
                <div className="app-sidebar__email-us-at-container">
                  <div className="app-sidebar__email-us-at">
                    Didn’t find your answer? Email us at
                  </div>
                  <a href="mailto:support@growegy.com">support@growegy.com</a>
                </div>
              ),
            },
          ]}
          menuButtonDataTest="side-bar__link-button"
          containerDataTest="side-bar__link"
          drop={isTabletOrLarger ? 'right' : 'up'}
          positionStrategy="fixed"
          customToggle={() => (
            <SideBarItem
              header="Help"
              icon={(props: { fill: string }) => <QuestionMark fill={props.fill} />}
              isActive={false}
            />
          )}
        />
      )}
    />
  );
};

const SideBarNavigation = (props: { user: ICurrentUser }) => {
  const { user } = props;
  const isTabletOrLarger = useIsTabletOrLarger();

  return (
    <nav className="app-sidebar__navigation">
      <div className="app-sidebar__navigation-items">
        {isTabletOrLarger ? (
          <>
            <SideBarGrowegyAI user={user} asHome={true} />
            <SideBarCalendar />
            <SideBarRecommendations user={user} />
            <SideBarSettings />
            <SideBarHelp />
          </>
        ) : (
          <>
            <SideBarSettings asHome={true} />
            <SideBarGrowegyAI user={user} />
            <SideBarCalendar />
            <SideBarHelp />
            <SideBarSignOut />
          </>
        )}
      </div>
    </nav>
  );
};

const SideBarSignOut = () => {
  const isTabletOrLarger = useIsTabletOrLarger();
  const user = useCurrentUser();
  const navigate = useNavigate();
  return (
    <VisibleResource
      to={AppRoute.home}
      render={() => (
        <GrowegyMenu
          menuItems={[
            {
              type: 'custom',
              component: () => (
                <div className="app-sidebar__email-us-at-container">
                  <div className="app-sidebar__account-info-container">
                    <span>{extractUserName(user)}</span>
                    <span className="app-sidebar__account-role">{user.role.name}</span>
                  </div>
                  <div className="app-sidebar__account-email">{user.email}</div>
                </div>
              ),
            },
            {
              type: 'divider',
            },
            {
              type: 'standard',
              text: 'Sign out',
              icon: <PowerIcon />,
              dataTest: 'app-sidebar__account-sign-out',
              onClick: () => navigate(AppRoute.signOut),
            },
          ]}
          menuButtonDataTest="side-bar__link-button"
          containerDataTest="side-bar__link"
          drop={isTabletOrLarger ? 'right' : 'up'}
          positionStrategy="fixed"
          customToggle={() => (
            <SideBarItem
              header="Account"
              icon={() => <UserAvatar user={user} />}
              isActive={false}
            />
          )}
        />
      )}
    />
  );
};

export const SideBar = () => {
  const {
    state: { user },
  } = useAuth();
  const isTabletOrLarger = useIsTabletOrLarger();

  return (
    user && (
      <div className="app-sidebar" data-test="app-sidebar">
        <SideBarLogo />
        <SideBarNavigation user={user} />
        {isTabletOrLarger && (
          <div className="app-sidebar__navigation-items" style={{ flex: '0 0 auto' }}>
            <SideBarSignOut />
          </div>
        )}
        <SanityChecker />
        <SmsNotificationsPrompt />
      </div>
    )
  );
};
